"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateEarliestAffectedCapitalizationMonth = exports.CapitalizationChangeReason = exports.getMonthYearPeriodSuperset = exports.dateStringToReadableMonthYearString = exports.applyCompletedEndMonthYearRule = exports.getMonthYearPeriodUsingDates = exports.getTaxYearMonthYearPeriod = exports.getCurrentFiscalYearMonthYearPeriod = exports.getEarliestAndLatestMonthYear = exports.formatTaxPeriod = exports.formatFiscalPeriod = exports.formatFiscalOrTaxPeriod = exports.getPeriodName = exports.getMonthPeriodName = exports.MONTH_PERIOD_NAMES = exports.getLastQuarterInPeriod = exports.formatMonthYearPeriod = exports.formatMonthYear = exports.calculateQuarter = exports.doPeriodsOverlap = exports.getMonthYearPeriodIntersection = exports.areSameMonthYearPeriod = exports.isPeriodFullYear = exports.getNumberOfMonthsInPeriod = exports.getNumMonthsBetweenInclusively = exports.areMonthYearEqual = exports.isMonthYearBefore = exports.minMonthYear = exports.maxMonthYear = exports.sameMonthYear = exports.getMonthName = exports.getMonthsBetweenAsc = exports.generateMonthYearInRange = exports.getLastDateStringOfMonthYear = exports.getLastDateOfMonthYear = exports.getFirstDateOfMonthYear = exports.getFirstDateStringOfMonthYear = exports.stringToMonthYear = exports.dateToMonthYearString = exports.toMonthYearString = exports.toMonthYearPeriod = exports.dateToMonthYear = exports.isMonthYearInRange = exports.isMonthYearAfterOrEqual = exports.isMonthYearBeforeOrEqual = exports.isMonthYear = void 0;
const dayjs_1 = __importDefault(require("dayjs"));
const utc_1 = __importDefault(require("dayjs/plugin/utc"));
const date_utils_1 = require("./date.utils");
const enums_1 = require("./enums");
const errors_1 = require("./errors");
dayjs_1.default.extend(utc_1.default);
const isMonthYear = (monthYear) => {
    if (!monthYear) {
        return false;
    }
    const { month, year } = monthYear;
    return typeof month === 'number' && typeof year === 'number';
};
exports.isMonthYear = isMonthYear;
const isMonthYearBeforeOrEqual = (a, b) => a.year < b.year || (a.year === b.year && a.month <= b.month);
exports.isMonthYearBeforeOrEqual = isMonthYearBeforeOrEqual;
const isMonthYearAfterOrEqual = (a, b) => a.year > b.year || (a.year === b.year && a.month >= b.month);
exports.isMonthYearAfterOrEqual = isMonthYearAfterOrEqual;
/**
 * Check whether the <month, year> is inclusively within the given range
 * @param monthYear - the pair of month year
 * @param startMonthYear - the start of the range (inclusive)
 * @param endMonthYear - the end of the range (inclusive)
 * @example
 * isMonthYearInRange({ month: 2, year: 2020 }, { month: 1, year: 2020 }, { month: 3, year: 2020 }) // true
 * isMonthYearInRange({ month: 6, year: 2020 }, { month: 1, year: 2020 }, { month: 3, year: 2020 }) // false
 */
const isMonthYearInRange = (monthYear, startMonthYear, endMonthYear) => {
    const isAfterStart = (0, exports.isMonthYearAfterOrEqual)(monthYear, startMonthYear);
    const isBeforeEnd = (0, exports.isMonthYearBeforeOrEqual)(monthYear, endMonthYear);
    return isAfterStart && isBeforeEnd;
};
exports.isMonthYearInRange = isMonthYearInRange;
/**
 * Convert a date to the <month, year> (ignoring the day of the month)
 */
const dateToMonthYear = (date) => {
    const result = new Date(date);
    result.setUTCDate(1);
    return {
        month: result.getUTCMonth(),
        year: result.getUTCFullYear(),
    };
};
exports.dateToMonthYear = dateToMonthYear;
const toMonthYearPeriod = (start, end) => {
    if (start > end) {
        throw (0, errors_1.createError)('InvalidPeriod', 'start must be before end');
    }
    return {
        startMonthYear: (0, exports.dateToMonthYear)(start),
        endMonthYear: (0, exports.dateToMonthYear)(end),
    };
};
exports.toMonthYearPeriod = toMonthYearPeriod;
/**
 * Get the YYYY-MM string of the {@link MonthYear} object.
 * @param monthYear - the month-year as a {@link MonthYear}
 * @returns a string in the format of YYYY-MM
 * @example
 * toMonthYearString({ month: 0, year: 2020 }) // '2020-01'
 * toMonthYearString({ month: 11, year: 2020 }) // '2020-12'
 */
const toMonthYearString = (monthYear) => {
    const month = (monthYear.month + 1).toString().padStart(2, '0');
    return `${monthYear.year}-${month}`;
};
exports.toMonthYearString = toMonthYearString;
const dateToMonthYearString = (date) => {
    const monthYear = (0, exports.dateToMonthYear)(date);
    return (0, exports.toMonthYearString)(monthYear);
};
exports.dateToMonthYearString = dateToMonthYearString;
/**
 * Get a {@link MonthYear} object from a YYYY-MM string.
 * @param monthYearString - a string in the format of YYYY-MM
 * @returns the month-year as a {@link MonthYear}
 * @example
 * stringToMonthYear('2020-01') // { month: 0, year: 2020 }
 * stringToMonthYear('2020-12') // { month: 11, year: 2020 }
 */
const stringToMonthYear = (monthYearString) => (0, exports.dateToMonthYear)(new Date(`${monthYearString}-01`));
exports.stringToMonthYear = stringToMonthYear;
/**
 * Get the first day of the month in the given <month, year> as a string
 */
const getFirstDateStringOfMonthYear = (monthYear) => {
    const monthYearString = (0, exports.toMonthYearString)(monthYear);
    return `${monthYearString}-01`;
};
exports.getFirstDateStringOfMonthYear = getFirstDateStringOfMonthYear;
/**
 * Get the first day of the month in the given <month, year> as a Date
 */
const getFirstDateOfMonthYear = (monthYear) => {
    const monthYearString = (0, exports.toMonthYearString)(monthYear);
    return dayjs_1.default.utc(`${monthYearString}-01`).toDate();
};
exports.getFirstDateOfMonthYear = getFirstDateOfMonthYear;
/**
 * Get the last day of the month in the given <month, year> as a Date
 */
const getLastDateOfMonthYear = (monthYear) => {
    const monthYearString = (0, exports.toMonthYearString)(monthYear);
    return dayjs_1.default.utc(`${monthYearString}-01`).endOf('month').toDate();
};
exports.getLastDateOfMonthYear = getLastDateOfMonthYear;
/**
 * Get the last day of the month in the given <month, year> as a string
 */
const getLastDateStringOfMonthYear = (monthYear) => {
    const monthYearString = typeof monthYear === 'string' ? monthYear : (0, exports.toMonthYearString)(monthYear);
    return dayjs_1.default.utc(`${monthYearString}-01`).endOf('month').format('YYYY-MM-DD');
};
exports.getLastDateStringOfMonthYear = getLastDateStringOfMonthYear;
const generateMonthYearInRange = (period) => {
    const nextMonthYear = (current) => ({
        year: current.month === 11 ? current.year + 1 : current.year,
        month: current.month === 11 ? 0 : current.month + 1,
    });
    const inner = (startMonthYear, endMonthYear) => (startMonthYear.year === endMonthYear.year && startMonthYear.month > endMonthYear.month) ||
        startMonthYear.year > endMonthYear.year
        ? []
        : [startMonthYear, ...inner(nextMonthYear(startMonthYear), endMonthYear)];
    return inner(period.startMonthYear, period.endMonthYear);
};
exports.generateMonthYearInRange = generateMonthYearInRange;
/**
 * @param startDate - inclusive
 * @param endDate - inclusive
 */
const getMonthsBetweenAsc = (startDate, endDate) => {
    if (startDate > endDate) {
        throw new Error('startDate must be before endDate');
    }
    const months = [];
    const date = new Date(startDate);
    while (date <= endDate) {
        date.setUTCDate(1); // set to first day of month to prevent overflow since not all months have the same number of days
        // casting since this is guaranteed to be in range 0-11, see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/getUTCMonth
        const month = date.getUTCMonth();
        months.push({ month, year: date.getUTCFullYear() });
        date.setUTCMonth(date.getUTCMonth() + 1);
    }
    return months;
};
exports.getMonthsBetweenAsc = getMonthsBetweenAsc;
// Any number between 2 and 27 is fine
// the purpose of this const is to avoid the unintended month set when calling date.setUTCDate
// e.g.
// date.setUTCDate(0, 1) can mean Jan 1st or Dec 31st depending on the time and the local timezone the code is running
// (05:00am on Jan 1st in UTC is Dec 31st in UTC-19)
const DAY_OF_MONTH = 15;
const getMonthName = (month, formatOption = 'short') => {
    const date = new Date();
    date.setUTCMonth(month, DAY_OF_MONTH);
    return date.toLocaleString('default', { month: formatOption });
};
exports.getMonthName = getMonthName;
const sameMonthYear = (a, b) => a.year === b.year && a.month === b.month;
exports.sameMonthYear = sameMonthYear;
/**
 * Given two month years return the greater month year
 */
const maxMonthYear = (a, b) => {
    if ((0, exports.isMonthYearBefore)(a, b)) {
        return b;
    }
    return a;
};
exports.maxMonthYear = maxMonthYear;
const minMonthYear = (a, b) => {
    if ((0, exports.isMonthYearBefore)(a, b)) {
        return a;
    }
    return b;
};
exports.minMonthYear = minMonthYear;
const isMonthYearBefore = (a, b) => {
    if (a.year < b.year) {
        return true;
    }
    else if (a.year > b.year) {
        return false;
    }
    return a.month < b.month;
};
exports.isMonthYearBefore = isMonthYearBefore;
const areMonthYearEqual = (a, b) => a.year === b.year && a.month === b.month;
exports.areMonthYearEqual = areMonthYearEqual;
/**
 * Get the number of months between two MonthYear objects inclusively.
 *
 * @deprecated use {@link getNumberOfMonthsInPeriod} instead
 */
const getNumMonthsBetweenInclusively = (a, b) => {
    const yearDiff = b.year - a.year;
    const monthDiff = b.month - a.month;
    return yearDiff * 12 + monthDiff + 1;
};
exports.getNumMonthsBetweenInclusively = getNumMonthsBetweenInclusively;
/**
 * Get the number of months between the time period (inclusive).
 *
 * @param monthYearPeriod - the period to calculate from
 * @returns number of months
 * @example calling getNumberOfMonthsInPeriod() on period { start: 2022/4, end: 2022/5 } returns 2
 * @example calling getNumberOfMonthsInPeriod() on period { start: 2021/10, end: 2022/5 } returns 8
 */
const getNumberOfMonthsInPeriod = (monthYearPeriod) => {
    const { endMonthYear, startMonthYear } = monthYearPeriod;
    const difference = 12 * (endMonthYear.year - startMonthYear.year) + (endMonthYear.month - startMonthYear.month);
    if (difference < 0) {
        throw (0, errors_1.createError)('InvalidPeriodError', 'Start of the period should be earlier than the end of it');
    }
    return difference + 1;
};
exports.getNumberOfMonthsInPeriod = getNumberOfMonthsInPeriod;
const isPeriodFullYear = (period) => {
    const num = (0, exports.getNumberOfMonthsInPeriod)(period);
    return num === 12;
};
exports.isPeriodFullYear = isPeriodFullYear;
const areSameMonthYearPeriod = (a, b) => a.startMonthYear.month === b.startMonthYear.month &&
    a.startMonthYear.year === b.startMonthYear.year &&
    a.endMonthYear.month === b.endMonthYear.month &&
    a.endMonthYear.year === b.endMonthYear.year;
exports.areSameMonthYearPeriod = areSameMonthYearPeriod;
/**
 * Determines the intersection of two MonthYearPeriods, which is defined as the maximum of the start MonthYears and the
 * minimum of the end MonthYears. If the maximum start MonthYear is later than the minimum end MonthYear, there is no
 * intersection.
 */
const getMonthYearPeriodIntersection = (periods) => {
    if (periods.length < 2) {
        throw new Error('Must provide at least two MonthYearPeriods to find intersection');
    }
    const periodIntersection = periods.reduce((memo, period) => {
        const startMonthYear = (0, exports.maxMonthYear)(memo.startMonthYear, period.startMonthYear);
        const endMonthYear = (0, exports.minMonthYear)(memo.endMonthYear, period.endMonthYear);
        return { startMonthYear, endMonthYear };
    }, { ...periods[0] });
    if ((0, exports.isMonthYearBefore)(periodIntersection.endMonthYear, periodIntersection.startMonthYear)) {
        throw new Error('No intersection between the given MonthYearPeriods');
    }
    return periodIntersection;
};
exports.getMonthYearPeriodIntersection = getMonthYearPeriodIntersection;
/**
 * Returns true if the two periods overlap, false otherwise.
 * @param period1 - the first period
 * @param period2 - the second period
 * @returns true if the periods overlap, false otherwise
 */
const doPeriodsOverlap = (period1, period2) => {
    const startMonthYear = (0, exports.maxMonthYear)(period1.startMonthYear, period2.startMonthYear);
    const endMonthYear = (0, exports.minMonthYear)(period1.endMonthYear, period2.endMonthYear);
    // If endMonthYear is before startMonthYear, there is no overlap
    return !(0, exports.isMonthYearBefore)(endMonthYear, startMonthYear);
};
exports.doPeriodsOverlap = doPeriodsOverlap;
/**
 * Returns the quarter number for a given month.
 * @param month represents a month in a year, 0-indexed like Date.getMonth()
 * @param fiscalYearEndMonth represents the last month in the year, 0-indexed like Date.getMonth()
 * @returns quarter number
 */
const calculateQuarter = (month, fiscalYearEndMonth = 11) => {
    const DECEMBER_ZERO_INDEXED = 11;
    const monthOffset = DECEMBER_ZERO_INDEXED - fiscalYearEndMonth;
    const normalizedMonth = ((month + monthOffset) % 12) + 1;
    return Math.ceil(normalizedMonth / 3);
};
exports.calculateQuarter = calculateQuarter;
/**
 * @example Jan '23
 */
const formatMonthYear = (monthYear) => {
    const date = (0, exports.getFirstDateOfMonthYear)(monthYear);
    const monthFormatted = date.toLocaleString('default', {
        month: 'short',
        timeZone: 'UTC', // required otherwise `toLocaleString` will use the local timezone
    });
    // Not using Intl.DateTimeFormatOptions for year because it doesn't support 2-digit year with apostrophe
    const yearFormatted = monthYear.year.toString().slice(-2);
    return `${monthFormatted} '${yearFormatted}`;
};
exports.formatMonthYear = formatMonthYear;
/**
 * @example { start: "Feb '22", end: "Jan '23" }
 */
const formatMonthYearPeriod = (period) => ({
    start: (0, exports.formatMonthYear)(period.startMonthYear),
    end: (0, exports.formatMonthYear)(period.endMonthYear),
});
exports.formatMonthYearPeriod = formatMonthYearPeriod;
const getLastQuarterInPeriod = (period, fiscalYearEndMonth) => {
    if (period.endMonthYear.month === fiscalYearEndMonth) {
        return enums_1.PeriodName.Q4;
    }
    if ((period.endMonthYear.month + 3 - fiscalYearEndMonth + 12) % 12 === 0) {
        return enums_1.PeriodName.Q3;
    }
    if ((period.endMonthYear.month + 6 - fiscalYearEndMonth + 12) % 12 === 0) {
        return enums_1.PeriodName.Q2;
    }
    if ((period.endMonthYear.month + 9 - fiscalYearEndMonth + 12) % 12 === 0) {
        return enums_1.PeriodName.Q1;
    }
    throw (0, errors_1.createError)('InvalidPeriodError', "Period doesn't end with a quarter");
};
exports.getLastQuarterInPeriod = getLastQuarterInPeriod;
exports.MONTH_PERIOD_NAMES = [
    enums_1.PeriodName.M1,
    enums_1.PeriodName.M2,
    enums_1.PeriodName.M3,
    enums_1.PeriodName.M4,
    enums_1.PeriodName.M5,
    enums_1.PeriodName.M6,
    enums_1.PeriodName.M7,
    enums_1.PeriodName.M8,
    enums_1.PeriodName.M9,
    enums_1.PeriodName.M10,
    enums_1.PeriodName.M11,
    enums_1.PeriodName.M12,
];
/**
 * Examples of fiscal month indexing:
 *
 * Case 1: December fiscal year end (fiscalYearEndMonth = 11)
 * - January (month = 0) becomes fiscal month 1
 * - February (month = 1) becomes fiscal month 2
 * - December (month = 11) becomes fiscal month 12
 *
 * Case 2: January fiscal year end (fiscalYearEndMonth = 0)
 * - February (month = 1) becomes fiscal month 1
 * - March (month = 2) becomes fiscal month 2
 * - January (month = 0) becomes fiscal month 12
 *
 * The formula (month + 11 - fiscalYearEndMonth) % 12 normalizes any month
 * to its corresponding fiscal month index (0-11) based on the fiscal year end.
 */
const getMonthPeriodName = (month, fiscalYearEndMonth) => {
    const fiscalMonthIndex = (month + 11 - fiscalYearEndMonth) % 12;
    return exports.MONTH_PERIOD_NAMES[fiscalMonthIndex];
};
exports.getMonthPeriodName = getMonthPeriodName;
/**
 * Validates and returns Year-to-Date (YTD) period based on fiscal year settings.
 *
 * For a period to be a valid YTD:
 * 1. It must start in the first month of the fiscal year
 *    - Example: If fiscal year ends in December (11), YTD must start in January (0)
 *    - Example: If fiscal year ends in March (2), YTD must start in April (3)
 *
 * 2. It must end in the expected month based on the number of months in the period
 *    - Example: For a 3-month YTD with December year-end:
 *      Start: January (0), End: March (2)
 *    - Example: For a 6-month YTD with March year-end:
 *      Start: April (3), End: September (8)
 *
 * @param period - The period to validate
 * @param fiscalYearEndMonth - The last month of the fiscal year (0-11, where 0 is January)
 * @param numberOfMonthsInPeriod - How many months the period should contain
 * @throws {InvalidPeriodError} If the period doesn't match expected start/end months
 * @returns {PeriodName.YTD} If the period is a valid YTD
 */
const returnYtdWhenValid = (period, fiscalYearEndMonth, numberOfMonthsInPeriod) => {
    const expectedStartMonth = (fiscalYearEndMonth + 1) % 12;
    const expectedEndMonth = (fiscalYearEndMonth + numberOfMonthsInPeriod + 12) % 12;
    if (period.startMonthYear.month !== expectedStartMonth) {
        throw (0, errors_1.createError)('InvalidPeriodError', `Start month of the period must be ${(0, date_utils_1.getMonthShortAbbreviation)(expectedStartMonth)} to be a valid period`);
    }
    if (period.endMonthYear.month !== expectedEndMonth) {
        throw (0, errors_1.createError)('InvalidPeriodError', `End month of the period must be ${(0, date_utils_1.getMonthShortAbbreviation)(expectedEndMonth)} to be a valid period`);
    }
    switch (numberOfMonthsInPeriod) {
        case 1:
            return enums_1.PeriodName.YTD1;
        case 2:
            return enums_1.PeriodName.YTD2;
        case 3:
            return enums_1.PeriodName.YTD3;
        case 4:
            return enums_1.PeriodName.YTD4;
        case 5:
            return enums_1.PeriodName.YTD5;
        case 6:
            return enums_1.PeriodName.YTD6;
        case 7:
            return enums_1.PeriodName.YTD7;
        case 8:
            return enums_1.PeriodName.YTD8;
        case 9:
            return enums_1.PeriodName.YTD9;
        case 10:
            return enums_1.PeriodName.YTD10;
        case 11:
            return enums_1.PeriodName.YTD11;
        case 12:
            return enums_1.PeriodName.YTD12;
        default:
            throw (0, errors_1.createError)('InvalidPeriodError', 'Period must be a valid period');
    }
};
const getPeriodName = (period, fiscalYearEndMonth) => {
    const numberOfMonthsInPeriod = (0, exports.getNumberOfMonthsInPeriod)(period);
    if (numberOfMonthsInPeriod > 12) {
        throw (0, errors_1.createError)('InvalidPeriodError', `invalid period: ${period}`);
    }
    switch (numberOfMonthsInPeriod) {
        // when the period is a full year
        case 12:
            if (period.endMonthYear.month !== fiscalYearEndMonth) {
                throw (0, errors_1.createError)('InvalidPeriodError', 'End month of the period must be the fiscal year end month');
            }
            return enums_1.PeriodName.A;
        case 11:
        case 10:
        case 9:
        case 8:
        case 7:
            return returnYtdWhenValid(period, fiscalYearEndMonth, numberOfMonthsInPeriod);
        // when the period is 6 months
        case 6: {
            const lastQuarter = (0, exports.getLastQuarterInPeriod)(period, fiscalYearEndMonth);
            switch (lastQuarter) {
                case enums_1.PeriodName.Q1:
                    throw (0, errors_1.createError)('InvalidPeriodError', 'Period must be a valid period');
                case enums_1.PeriodName.Q2:
                    return enums_1.PeriodName.H1;
                case enums_1.PeriodName.Q3:
                    throw (0, errors_1.createError)('InvalidPeriodError', 'Period must be a valid period');
                case enums_1.PeriodName.Q4:
                    return enums_1.PeriodName.H2;
                default:
                    throw (0, errors_1.createError)('InvalidPeriodError', 'Period must be a valid period');
            }
        }
        case 5:
        case 4:
            return returnYtdWhenValid(period, fiscalYearEndMonth, numberOfMonthsInPeriod);
        // when the period is 3 months
        case 3:
            return (0, exports.getLastQuarterInPeriod)(period, fiscalYearEndMonth);
        case 2:
            return returnYtdWhenValid(period, fiscalYearEndMonth, numberOfMonthsInPeriod);
        // when the period is 1 month
        case 1:
            return (0, exports.getMonthPeriodName)(period.startMonthYear.month, fiscalYearEndMonth);
        default:
            throw (0, errors_1.createError)('InvalidPeriodError', 'Period must be a valid period');
    }
};
exports.getPeriodName = getPeriodName;
const formatFiscalOrTaxPeriod = (period, fiscalYearEndMonth, periodType) => {
    const numberOfMonthsInPeriod = (0, exports.getNumberOfMonthsInPeriod)(period);
    const periodLabel = periodType === 'fiscal' ? 'FY' : 'TY';
    const year = periodType === 'fiscal' ? period.endMonthYear.year : period.startMonthYear.year;
    const yearLabel = `${periodLabel} ${year}`;
    switch (numberOfMonthsInPeriod) {
        // Quarter
        case 3: {
            const quarter = (0, exports.calculateQuarter)(period.endMonthYear.month, fiscalYearEndMonth);
            return {
                periodLabel,
                year,
                yearLabel,
                quarter,
                quarterLabel: `Q${quarter}`,
            };
        }
        // Year
        case 12:
            return {
                periodLabel,
                year,
                yearLabel,
                quarter: null,
                quarterLabel: null,
            };
        default:
            throw (0, errors_1.createError)('UnexpectedFiscalOrTaxPeriod', `Unexpected number of months in period: ${numberOfMonthsInPeriod}`);
    }
};
exports.formatFiscalOrTaxPeriod = formatFiscalOrTaxPeriod;
const formatFiscalPeriod = (period, fiscalYearEndMonth) => (0, exports.formatFiscalOrTaxPeriod)(period, fiscalYearEndMonth, 'fiscal');
exports.formatFiscalPeriod = formatFiscalPeriod;
const formatTaxPeriod = (period, fiscalYearEndMonth) => (0, exports.formatFiscalOrTaxPeriod)(period, fiscalYearEndMonth, 'tax');
exports.formatTaxPeriod = formatTaxPeriod;
const getEarliestAndLatestMonthYear = (monthYears) => {
    if (monthYears.length === 0) {
        throw (0, errors_1.createError)('MissingMonthYearError', 'Must provide at least one MonthYear');
    }
    let earliestMonthYear = monthYears[0];
    let latestMonthYear = monthYears[0];
    for (const monthYear of monthYears) {
        if ((0, exports.isMonthYearBefore)(monthYear, earliestMonthYear)) {
            earliestMonthYear = monthYear;
        }
        if ((0, exports.isMonthYearBefore)(latestMonthYear, monthYear)) {
            latestMonthYear = monthYear;
        }
    }
    return {
        startMonthYear: earliestMonthYear,
        endMonthYear: latestMonthYear,
    };
};
exports.getEarliestAndLatestMonthYear = getEarliestAndLatestMonthYear;
const getCurrentFiscalYearMonthYearPeriod = (yearEndDateMonth) => {
    const currentDate = (0, date_utils_1.getCurrentDateObject)();
    const currentYear = currentDate.getFullYear();
    const taxYearEndDateThisYear = (0, date_utils_1.getLastDayOfTheMonth)(new Date(currentYear, yearEndDateMonth, 1));
    const taxYearEndDateLastYear = (0, date_utils_1.getLastDayOfTheMonth)(new Date(currentYear - 1, yearEndDateMonth, 1));
    const currentTaxYearEndDate = taxYearEndDateThisYear > currentDate ? taxYearEndDateLastYear : taxYearEndDateThisYear;
    const currentTaxEndYear = currentTaxYearEndDate.getFullYear();
    const currentTaxYearEndMonth = currentTaxYearEndDate.getMonth();
    const startYear = currentTaxYearEndMonth === enums_1.DateMonth.December ? currentTaxEndYear : currentTaxEndYear - 1;
    const endYear = currentTaxEndYear;
    const startMonth = (yearEndDateMonth + 1) % 12;
    const startMonthYear = { month: startMonth, year: startYear };
    const endMonthYear = { month: yearEndDateMonth, year: endYear };
    return { startMonthYear, endMonthYear };
};
exports.getCurrentFiscalYearMonthYearPeriod = getCurrentFiscalYearMonthYearPeriod;
const getTaxYearMonthYearPeriod = (yearEndDateMonth, taxYear) => {
    const startMonth = (yearEndDateMonth + 1) % 12;
    const startMonthYear = { month: startMonth, year: taxYear };
    const endYear = yearEndDateMonth === enums_1.DateMonth.December ? taxYear : taxYear + 1;
    const endMonthYear = { month: yearEndDateMonth, year: endYear };
    return { startMonthYear, endMonthYear };
};
exports.getTaxYearMonthYearPeriod = getTaxYearMonthYearPeriod;
const getMonthYearPeriodUsingDates = (startDate, endDate) => {
    const startMonth = new Date(startDate).getUTCMonth();
    const startYear = new Date(startDate).getUTCFullYear();
    const endMonth = new Date(endDate).getUTCMonth();
    const endYear = new Date(endDate).getUTCFullYear();
    const startMonthYear = { month: startMonth, year: startYear };
    const endMonthYear = { month: endMonth, year: endYear };
    return { startMonthYear, endMonthYear };
};
exports.getMonthYearPeriodUsingDates = getMonthYearPeriodUsingDates;
const applyCompletedEndMonthYearRule = (monthYearPeriod) => {
    const currentDate = (0, date_utils_1.getCurrentDateObject)();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const currentMonthYear = {
        month: currentMonth,
        year: currentYear,
    };
    if ((0, exports.isMonthYearBefore)(currentMonthYear, monthYearPeriod.startMonthYear)) {
        throw (0, errors_1.createError)('CurrentEndMonthYearError', `Today's month-year ${currentMonthYear.month}-${currentMonthYear.year} before start month-year ${monthYearPeriod.startMonthYear.month}-${monthYearPeriod.startMonthYear.year}`);
    }
    if ((0, exports.isMonthYearBefore)(currentMonthYear, monthYearPeriod.endMonthYear)) {
        return {
            startMonthYear: monthYearPeriod.startMonthYear,
            endMonthYear: currentMonthYear,
        };
    }
    return monthYearPeriod;
};
exports.applyCompletedEndMonthYearRule = applyCompletedEndMonthYearRule;
const dateStringToReadableMonthYearString = (dateString) => {
    const datejs = dayjs_1.default.utc(dateString);
    return datejs.format('MMMM YYYY');
};
exports.dateStringToReadableMonthYearString = dateStringToReadableMonthYearString;
/**
 * Determines the superset (union) of MonthYearPeriods, which is defined as the minimum of the start MonthYears and the
 * maximum of the end MonthYears. This creates a single period that encompasses all the provided periods.
 *
 * @param periods - Array of MonthYearPeriods to find the superset of
 * @returns A single MonthYearPeriod that encompasses all input periods
 * @throws Error if the input array is empty
 */
const getMonthYearPeriodSuperset = (periods) => {
    if (periods.length === 0) {
        throw (0, errors_1.createError)('MissingMonthYearPeriodError', 'Must provide at least one MonthYearPeriod to find superset');
    }
    if (periods.length === 1) {
        return { ...periods[0] };
    }
    const periodSuperset = periods.reduce((acc, period) => {
        const startMonthYear = (0, exports.minMonthYear)(acc.startMonthYear, period.startMonthYear);
        const endMonthYear = (0, exports.maxMonthYear)(acc.endMonthYear, period.endMonthYear);
        return { startMonthYear, endMonthYear };
    }, { ...periods[0] });
    return periodSuperset;
};
exports.getMonthYearPeriodSuperset = getMonthYearPeriodSuperset;
var CapitalizationChangeReason;
(function (CapitalizationChangeReason) {
    CapitalizationChangeReason["MONTHS_NOW_CAPITALIZED"] = "MONTHS_NOW_CAPITALIZED";
    CapitalizationChangeReason["MONTHS_NO_LONGER_CAPITALIZED"] = "MONTHS_NO_LONGER_CAPITALIZED";
    CapitalizationChangeReason["CAPITALIZATION_PERIOD_CHANGED"] = "CAPITALIZATION_PERIOD_CHANGED";
})(CapitalizationChangeReason || (exports.CapitalizationChangeReason = CapitalizationChangeReason = {}));
/**
 * Calculates the earliest month that would be affected by a capitalization period change.
 * Returns null if no approved months would be affected.
 *
 * This is used to determine which approval months need to be reset when capitalization
 * dates are modified.
 *
 * @returns The earliest affected month-year with reason, or null if no approvals would be affected
 */
const calculateEarliestAffectedCapitalizationMonth = (input) => {
    const { approvedThrough, newCapitalizationEnd, newCapitalizationStart, oldCapitalizationEnd, oldCapitalizationStart, } = input;
    // If capitalization is being disabled entirely (both dates become null), clear all approvals
    if (!newCapitalizationStart && !newCapitalizationEnd && (oldCapitalizationStart || oldCapitalizationEnd)) {
        // Return the earliest possible month (will clear all)
        return {
            monthYear: oldCapitalizationStart ?? { month: 1, year: 1970 },
            reason: CapitalizationChangeReason.MONTHS_NO_LONGER_CAPITALIZED,
        };
    }
    // If there's no change, no clearing needed
    if (oldCapitalizationStart?.month === newCapitalizationStart?.month &&
        oldCapitalizationStart?.year === newCapitalizationStart?.year &&
        oldCapitalizationEnd?.month === newCapitalizationEnd?.month &&
        oldCapitalizationEnd?.year === newCapitalizationEnd?.year) {
        return null;
    }
    // If old dates were null but new dates exist, check if new capitalization is in approved range
    if (!oldCapitalizationStart && newCapitalizationStart) {
        // When transitioning from expensed to capitalized, we need to reset approvals
        // for any months that will now become capitalized within the approved range
        if ((0, exports.isMonthYearBeforeOrEqual)(newCapitalizationStart, approvedThrough)) {
            return {
                monthYear: newCapitalizationStart,
                reason: CapitalizationChangeReason.MONTHS_NOW_CAPITALIZED,
            };
        }
        return null;
    }
    // If old start was null and new start is also null, no start date changes to track
    if (!oldCapitalizationStart) {
        return null;
    }
    let earliestAffected = null;
    let earliestReason = null;
    // Check for changes at the capitalization start
    if (newCapitalizationStart && !(0, exports.areMonthYearEqual)(newCapitalizationStart, oldCapitalizationStart)) {
        // Capitalization start date changed - earliest affected is the earlier of old and new start
        earliestAffected = (0, exports.minMonthYear)(oldCapitalizationStart, newCapitalizationStart);
        earliestReason = CapitalizationChangeReason.CAPITALIZATION_PERIOD_CHANGED;
    }
    // Check for changes at the capitalization end (shrinking)
    if (oldCapitalizationEnd && newCapitalizationEnd && (0, exports.isMonthYearBefore)(newCapitalizationEnd, oldCapitalizationEnd)) {
        // Capitalization end date moved earlier (shrinking) - first removed month is one after new end
        // Note: months are 0-indexed in MonthYear (0-11)
        const firstRemovedMonth = newCapitalizationEnd.month === 11 ? 0 : newCapitalizationEnd.month + 1;
        const firstRemovedYear = newCapitalizationEnd.month === 11 ? newCapitalizationEnd.year + 1 : newCapitalizationEnd.year;
        const firstRemoved = { month: firstRemovedMonth, year: firstRemovedYear };
        // Only consider if this is in the approved range (firstRemoved <= approvedThrough)
        if (!(0, exports.isMonthYearBefore)(approvedThrough, firstRemoved)) {
            if (!earliestAffected || (0, exports.isMonthYearBefore)(firstRemoved, earliestAffected)) {
                earliestAffected = firstRemoved;
                earliestReason = CapitalizationChangeReason.MONTHS_NO_LONGER_CAPITALIZED;
            }
        }
    }
    // Check for changes at the capitalization end (expanding)
    if (oldCapitalizationEnd && newCapitalizationEnd && (0, exports.isMonthYearBefore)(oldCapitalizationEnd, newCapitalizationEnd)) {
        // Capitalization end date moved later (expanding) - first added month is one after old end
        const firstAddedMonth = oldCapitalizationEnd.month === 11 ? 0 : oldCapitalizationEnd.month + 1;
        const firstAddedYear = oldCapitalizationEnd.month === 11 ? oldCapitalizationEnd.year + 1 : oldCapitalizationEnd.year;
        const firstAdded = { month: firstAddedMonth, year: firstAddedYear };
        // Only consider if this is in the approved range (firstAdded <= approvedThrough)
        if (!(0, exports.isMonthYearBefore)(approvedThrough, firstAdded)) {
            if (!earliestAffected || (0, exports.isMonthYearBefore)(firstAdded, earliestAffected)) {
                earliestAffected = firstAdded;
                earliestReason = CapitalizationChangeReason.MONTHS_NOW_CAPITALIZED;
            }
        }
    }
    // Check for end date removal (value → null)
    // When end date is removed, months after old end become capitalized
    if (oldCapitalizationEnd && !newCapitalizationEnd && newCapitalizationStart) {
        const firstAddedMonth = oldCapitalizationEnd.month === 11 ? 0 : oldCapitalizationEnd.month + 1;
        const firstAddedYear = oldCapitalizationEnd.month === 11 ? oldCapitalizationEnd.year + 1 : oldCapitalizationEnd.year;
        const firstAdded = { month: firstAddedMonth, year: firstAddedYear };
        // Only consider if this is in the approved range (firstAdded <= approvedThrough)
        if (!(0, exports.isMonthYearBefore)(approvedThrough, firstAdded)) {
            if (!earliestAffected || (0, exports.isMonthYearBefore)(firstAdded, earliestAffected)) {
                earliestAffected = firstAdded;
                earliestReason = CapitalizationChangeReason.MONTHS_NOW_CAPITALIZED;
            }
        }
    }
    // Check for end date addition (null → value)
    // When end date is added, months after new end are no longer capitalized
    if (!oldCapitalizationEnd && newCapitalizationEnd && oldCapitalizationStart) {
        const firstRemovedMonth = newCapitalizationEnd.month === 11 ? 0 : newCapitalizationEnd.month + 1;
        const firstRemovedYear = newCapitalizationEnd.month === 11 ? newCapitalizationEnd.year + 1 : newCapitalizationEnd.year;
        const firstRemoved = { month: firstRemovedMonth, year: firstRemovedYear };
        // Only consider if this is in the approved range (firstRemoved <= approvedThrough)
        if (!(0, exports.isMonthYearBefore)(approvedThrough, firstRemoved)) {
            if (!earliestAffected || (0, exports.isMonthYearBefore)(firstRemoved, earliestAffected)) {
                earliestAffected = firstRemoved;
                earliestReason = CapitalizationChangeReason.MONTHS_NO_LONGER_CAPITALIZED;
            }
        }
    }
    // If no affected months or earliest affected is after approved range, no clearing needed
    if (!earliestAffected || !earliestReason || (0, exports.isMonthYearBefore)(approvedThrough, earliestAffected)) {
        return null;
    }
    return { monthYear: earliestAffected, reason: earliestReason };
};
exports.calculateEarliestAffectedCapitalizationMonth = calculateEarliestAffectedCapitalizationMonth;
